import { useEffect, useRef, useState, type ReactNode } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Camera, DraftingCompass, FileSpreadsheet, Trash2, Upload } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/Spinner";
import {
  deleteMdcpPointPhoto,
  downloadMdcpOrderPoints,
  getMdcpOrderPoints,
  uploadMdcpPointPhoto,
} from "../api";
import type { MdcpPhotoPoint } from "../types";
import { apiErr } from "../utils";
import { LinePlacementLegend } from "./LinePlacementLegend";
import { buildMdcpLightbox, MdcpPhotoLightbox } from "./MdcpPhotoLightbox";
import {
  MdcpPointPhotoGrid,
  MdcpPointsFootnote,
  MdcpPointsList,
  MdcpSelectedPointHeader,
} from "./MdcpPointsParts";
import { drawMdcpContext, mdcpPointIcon } from "./mdcpMap";

/** Sekcja „Punkty zdjęciowe MDCP" w zamówieniu geodety — ten sam układ, co po naszej stronie
 *  (MdcpPointsPanel): mapa z trasą, działkami i punktami ze strzałkami azymutu, obok panel
 *  wybranego punktu, a pod mapą lista punktów z dokładnymi współrzędnymi PUWG i przyciskiem
 *  „Wgraj" w każdym wierszu. Różni się tylko akcjami: zamiast odbioru zdjęć geodeta ma
 *  aparat/galerię (telefon), pobranie punktów do CSV/DXF (do własnego oprogramowania), a
 *  wybrany punkt przeżywa przeładowanie strony. Zdjęcia wpinają się dokładnie do punktu,
 *  więc odbiór po naszej stronie widzi ten sam materiał. */

// Wybrany punkt przeżywa przeładowanie strony: Chrome na Androidzie potrafi ubić kartę,
// gdy w tle otwarty jest aparat — po powrocie ze zdjęciem strona wstaje od nowa i bez
// tego geodeta ląduje na „Kliknij punkt na mapie", myśląc, że przycisk nie działa.
const selectionKey = (orderId: string) => `elg5:mdcp-selected-point:${orderId}`;

function readSelection(orderId: string): string | null {
  try {
    return sessionStorage.getItem(selectionKey(orderId));
  } catch {
    return null;
  }
}

function writeSelection(orderId: string, pointId: string) {
  try {
    sessionStorage.setItem(selectionKey(orderId), pointId);
  } catch {
    // Prywatne okno / zablokowany storage — wybór po prostu nie przeżyje reloadu.
  }
}

export function MdcpContractorMap({
  orderId,
  readOnly = false,
}: {
  orderId: string;
  // Podgląd „oczami podwykonawcy" u wewnętrznych: mapa, lista, zdjęcia I kontrolki
  // geodety widoczne tak samo jak u niego, tylko wyłączone — podgląd ma być pasywny,
  // ale nie może chować opcji, które podwykonawca ma.
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const key = ["pp", "mdcp-order-points", orderId] as const;
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => getMdcpOrderPoints(orderId),
    retry: false,
    staleTime: 30_000,
  });
  const points = data?.points;
  const inval = () => qc.invalidateQueries({ queryKey: key });

  const [selectedId, setSelectedId] = useState<string | null>(() => readSelection(orderId));
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const uploadM = useMutation({
    mutationFn: async (v: { pointId: string; files: File[] }) => {
      for (const file of v.files) await uploadMdcpPointPhoto(v.pointId, file);
    },
    onSuccess: inval,
    onError: (e) => setUploadError(apiErr(e)),
  });
  const deleteM = useMutation({
    mutationFn: (photoId: string) => deleteMdcpPointPhoto(photoId),
    onSuccess: inval,
  });
  // Eksport jest pasywny (nic nie zmienia), więc działa też w podglądzie tylko-do-odczytu.
  const [exportError, setExportError] = useState<string | null>(null);
  const exportM = useMutation({
    mutationFn: (fmt: "csv" | "dxf") => downloadMdcpOrderPoints(orderId, fmt),
    onMutate: () => setExportError(null),
    onError: (e) => setExportError(apiErr(e)),
  });

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contextGroupRef = useRef<L.LayerGroup | null>(null);
  const groupRef = useRef<L.LayerGroup | null>(null);
  // Bounds of the route/parcels/buffer underlay — the first fit covers them AND the points,
  // exactly like our side, so the surveyor sees the whole job, not just the markers.
  const contextBoundsRef = useRef<L.LatLngBounds | null>(null);
  const fittedRef = useRef(false);
  // Latest selection reachable from Leaflet click handlers without re-binding markers.
  const selectRef = useRef<(id: string) => void>(() => {});
  selectRef.current = (id) => {
    setSelectedId(id);
    setUploadError(null);
    writeSelection(orderId, id);
  };

  // Init the map once the container is mounted (after the loading gate clears).
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { center: [52, 19], zoom: 6, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);
    contextGroupRef.current = L.layerGroup().addTo(map);
    groupRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [isLoading]);

  // Route + parcels underlay — the same view-only context we see on our side.
  useEffect(() => {
    const group = contextGroupRef.current;
    if (!group || !data) return;
    group.clearLayers();
    contextBoundsRef.current = drawMdcpContext(group, data);
  }, [data]);

  // Draw the frozen points + fit to them (and the underlay) once.
  useEffect(() => {
    const map = mapRef.current;
    const group = groupRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const latlngs: L.LatLngExpression[] = [];
    for (const p of points ?? []) {
      if (!p.geometry) continue;
      const [lng, lat] = p.geometry.coordinates as [number, number];
      latlngs.push([lat, lng]);
      const m = L.marker([lat, lng], { icon: mdcpPointIcon(p, p.id === selectedId), zIndexOffset: 1000 });
      m.on("click", () => selectRef.current(p.id));
      m.bindTooltip(
        `Punkt #${p.number} · ${p.azimuth_deg}°${p.note ? ` · ${p.note}` : ""}` +
          (p.photos_count > 0 ? ` · ${p.photos_count} zdj.` : ""),
      );
      m.addTo(group);
    }
    if (!fittedRef.current && latlngs.length > 0) {
      // Fit PO layoucie: w tym samym ticku co montaż kontenera Leaflet liczy widok
      // na złych wymiarach i mapa zostaje na ogólnym planie (bug widoczny przy
      // osadzeniu w "Mapie projektów" podwykonawcy).
      const timer = window.setTimeout(() => {
        // Flaga dopiero po UDANYM dopasowaniu — re-run efektu w oknie 80 ms
        // kasuje timer i planuje fit od nowa, zamiast go bezpowrotnie gubić.
        fittedRef.current = true;
        map.invalidateSize();
        const ctx = contextBoundsRef.current;
        const bounds = ctx ? latlngs.reduce((b, ll) => b.extend(ll), ctx) : L.latLngBounds(latlngs);
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
      }, 80);
      return () => window.clearTimeout(timer);
    }
  }, [points, selectedId]);

  if (isLoading)
    return (
      <div className="py-6 flex justify-center">
        <Spinner />
      </div>
    );
  // No MDCP points on this order → the panel self-hides (same contract as MdcpPointsPanel).
  if (isError || !data || !points || points.length === 0) return null;

  const selected = points.find((p) => p.id === selectedId) ?? null;
  const withPhoto = points.filter((p) => p.photos_count > 0).length;
  const rejectedCount = points.reduce((n, p) => n + p.rejected_photos_count, 0);

  // Flat photo list (all points) — powers the lightbox, same captions as on our side.
  const allPhotos = buildMdcpLightbox(points);
  const photoIndexById = new Map(allPhotos.map((ph, i) => [ph.id, i]));

  const selectFromList = (p: MdcpPhotoPoint) => {
    selectRef.current(p.id);
    if (p.geometry && mapRef.current) {
      const [lng, lat] = p.geometry.coordinates as [number, number];
      mapRef.current.panTo([lat, lng]);
    }
  };

  // Wgrywka z wiersza listy lub z panelu: punkt staje się wybrany (i zapamiętany na wypadek
  // przeładowania), a jego zdjęcia lecą po kolei.
  const startUpload = (pointId: string, files: File[]) => {
    if (files.length === 0) return;
    selectRef.current(pointId);
    uploadM.mutate({ pointId, files });
  };

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold border-l-2 border-[var(--color-primary)] pl-2">
        <Camera size={15} /> Punkty zdjęciowe MDCP
        <span className="text-xs font-normal text-[var(--color-text-muted)]">
          {withPhoto}/{points.length} ze zdjęciem
          {rejectedCount > 0 ? ` · ${rejectedCount} odrzuconych — do powtórzenia` : ""}
        </span>
        <span className="ml-auto" />
        {/* Punkty do własnego oprogramowania geodety: zestawienie CSV (X = północ, Y = wschód,
            PUWG 2000 strefy projektu) i DXF z naniesionymi punktami w tym samym układzie. */}
        <button
          type="button"
          disabled={exportM.isPending}
          onClick={() => exportM.mutate("csv")}
          title="Pobierz zestawienie punktów (Nr, X, Y w PUWG 2000, azymut, uwagi) jako CSV"
          className="inline-flex items-center gap-1.5 border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-normal hover:bg-[var(--color-bg-elevated)] disabled:opacity-60"
        >
          {exportM.isPending && exportM.variables === "csv" ? <Spinner /> : <FileSpreadsheet size={12} />} CSV
        </button>
        <button
          type="button"
          disabled={exportM.isPending}
          onClick={() => exportM.mutate("dxf")}
          title="Pobierz punkty naniesione w DXF (układ PUWG 2000 strefy projektu, warstwy MDCP_*)"
          className="inline-flex items-center gap-1.5 border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-normal hover:bg-[var(--color-bg-elevated)] disabled:opacity-60"
        >
          {exportM.isPending && exportM.variables === "dxf" ? <Spinner /> : <DraftingCompass size={12} />} DXF
        </button>
      </h2>
      {exportError && (
        <p className="text-[11px] font-mono text-[var(--color-primary)]">{exportError}</p>
      )}

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="lg:col-span-2 relative isolate z-0 space-y-1">
          <div
            ref={containerRef}
            className="w-full border border-[var(--color-border)]"
            style={{ background: "#fff", height: "380px" }}
          />
          {/* Rodzaj linii z trasy projektu — geodeta ma wiedzieć, czy fotografuje przebieg
              kabla w ziemi (linia ciągła), czy linię napowietrzną (przerywana). */}
          <LinePlacementLegend summary={data.route?.line_placement} />
        </div>

        {/* Klik w punkt (mapa lub lista) → jego zdjęcia tutaj, po prawej. */}
        <div className="border border-[var(--color-border)] p-3 space-y-3 min-h-[120px]">
          {selected ? (
            <>
              <MdcpSelectedPointHeader point={selected} />
              <MdcpPointPhotoGrid
                photos={selected.photos}
                onOpen={(ph) => setLightboxIndex(photoIndexById.get(ph.id) ?? 0)}
                overlay={(ph) => (
                  <button
                    type="button"
                    title={readOnly ? "Podgląd — akcje niedostępne" : "Usuń zdjęcie"}
                    disabled={readOnly || deleteM.isPending}
                    onClick={() => deleteM.mutate(ph.id)}
                    className="bg-white/90 p-0.5 text-[var(--color-text-muted)] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              />
              {(
                <div className="space-y-1.5">
                  {/* Dwa wejścia zamiast jednego z `capture`: Chrome na Androidzie przy `capture`
                      odpala aparat intentem i wymaga zgody na aparat dla samej przeglądarki —
                      po odmowie (zwłaszcza „nie pytaj ponownie") kliknięcie NIC nie robi, bez
                      komunikatu. Wybór z galerii idzie systemowym wyborem plików i działa zawsze. */}
                  <div className="flex flex-wrap gap-2">
                    <UploadButton
                      label="Zrób zdjęcie"
                      icon={<Camera size={14} />}
                      capture="environment"
                      pending={uploadM.isPending}
                      disabled={readOnly}
                      onFiles={(files) => startUpload(selected.id, files)}
                    />
                    <UploadButton
                      label="Wybierz z galerii"
                      icon={<Upload size={14} />}
                      multiple
                      pending={uploadM.isPending}
                      disabled={readOnly}
                      onFiles={(files) => startUpload(selected.id, files)}
                    />
                  </div>
                  <p className="text-[11px] text-[var(--color-text-muted)]">
                    Jeśli po „Zrób zdjęcie" aparat się nie otwiera, przeglądarka nie ma zgody na
                    aparat — zezwól na nią w ustawieniach strony albo wybierz zdjęcie z galerii.
                  </p>
                </div>
              )}
              {uploadError && (
                <p className="text-[11px] font-mono text-[var(--color-primary)]">{uploadError}</p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Kliknij punkt na mapie (lub na liście poniżej), aby zobaczyć
              {readOnly ? "" : " i wgrać"} jego zdjęcia.
            </p>
          )}
        </div>
      </div>

      <MdcpPointsList
        points={points}
        selectedId={selectedId}
        onSelect={selectFromList}
        rowAction={(p) => (
          <UploadButton
            label="Wgraj"
            icon={<Upload size={11} />}
            compact
            multiple
            pending={uploadM.isPending}
            disabled={readOnly}
            onFiles={(files) => startUpload(p.id, files)}
          />
        )}
      />

      <MdcpPointsFootnote points={points} />

      {lightboxIndex !== null && allPhotos.length > 0 && (
        <MdcpPhotoLightbox
          photos={allPhotos}
          index={Math.min(lightboxIndex, allPhotos.length - 1)}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </section>
  );
}

/** Przycisk-etykieta z ukrytym `<input type="file">`. Z `capture` telefon otwiera aparat,
 *  bez — systemowy wybór pliku/galerię. Wartość inputu zerujemy po każdym wyborze, żeby to
 *  samo zdjęcie dało się wgrać ponownie (np. po błędzie sieci). Klik nie bąbelkuje — w wierszu
 *  listy nie ma przesuwać mapy, tylko otworzyć wybór pliku. */
function UploadButton({
  label,
  icon,
  capture,
  multiple = false,
  compact = false,
  pending,
  disabled = false,
  onFiles,
}: {
  label: string;
  icon: ReactNode;
  capture?: "environment" | "user";
  multiple?: boolean;
  compact?: boolean;
  pending: boolean;
  /** Podgląd GK: kontrolka widoczna, ale wybór pliku wyłączony. */
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}) {
  return (
    <label
      title={disabled ? "Podgląd — akcje niedostępne" : undefined}
      className={`inline-flex items-center gap-1.5 border border-[var(--color-border)] select-none ${
        compact ? "px-2 py-0.5 text-[11px]" : "px-3 py-1.5 text-sm"
      } ${
        disabled
          ? "opacity-60 cursor-not-allowed"
          : pending
            ? "opacity-60 cursor-wait"
            : "cursor-pointer hover:bg-[var(--color-bg-elevated)]"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="file"
        accept="image/*"
        capture={capture}
        multiple={multiple}
        className="hidden"
        disabled={pending || disabled}
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      {pending ? <Spinner /> : icon} {label}
    </label>
  );
}
